import { useEffect, useState } from "react";
import { http, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Check, X, MapPin, ExternalLink, ShieldCheck, CalendarDays, Users, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";

function Card({ s, act, busy }) {
  return (
    <div data-testid="admin-submission-card" className="rounded-2xl bg-white border border-black/5 shadow-sm overflow-hidden sm:flex">
      {s.photo_url ? (
        <img src={s.photo_url} alt={s.name} className="sm:w-48 w-full h-40 object-cover" data-testid="admin-submission-photo" />
      ) : (
        <div className="sm:w-48 w-full h-40 bg-[#0E395F]/5 grid place-items-center text-black/25"><MapPin size={28} /></div>
      )}
      <div className="p-5 flex-1">
        <div className="font-black pv-text-dark text-lg">{s.name}</div>
        <div className="text-sm text-black/50 flex items-center gap-1"><MapPin size={13} />{s.address}</div>
        <div className="flex flex-wrap gap-2 mt-2 text-xs">
          <span className="rounded-full bg-[#4A90E2]/10 text-[#4A90E2] font-bold px-2.5 py-1">{s.courts_count} courts</span>
          {s.surface && <span className="rounded-full bg-black/5 text-black/60 px-2.5 py-1">{s.surface}</span>}
          {s.website && <a href={s.website} target="_blank" rel="noreferrer" className="rounded-full bg-black/5 text-[#4A90E2] px-2.5 py-1 inline-flex items-center gap-1">Website <ExternalLink size={11} /></a>}
        </div>
        {s.notes && <p className="text-sm text-black/60 mt-2">{s.notes}</p>}
        <div className="text-xs text-black/40 mt-2">Submitted by {s.submitter_name || s.submitter_email}</div>
        <div className="flex gap-3 mt-4">
          <Button data-testid={`approve-btn-${s.submission_id}`} onClick={() => act(s, "approve")} disabled={!!busy} className="bg-[#005F19] text-white font-bold rounded-full px-5 hover:opacity-90">
            {busy === s.submission_id + "approve" ? <Loader2 size={15} className="animate-spin mr-2" /> : <Check size={15} className="mr-2" />}Approve
          </Button>
          <Button data-testid={`decline-btn-${s.submission_id}`} onClick={() => act(s, "decline")} disabled={!!busy} variant="outline" className="border-2 border-black/15 text-black/60 font-bold rounded-full px-5 hover:bg-black/5">
            {busy === s.submission_id + "decline" ? <Loader2 size={15} className="animate-spin mr-2" /> : <X size={15} className="mr-2" />}Decline
          </Button>
        </div>
      </div>
    </div>
  );
}

function ReviewedRow({ s }) {
  const ok = s.status === "approved";
  return (
    <div data-testid="admin-reviewed-item" className="flex items-center justify-between gap-3 rounded-xl border border-black/5 bg-white px-4 py-3">
      <div className="min-w-0">
        <div className="font-bold pv-text-dark text-sm truncate">{s.name}</div>
        <div className="text-xs text-black/50 truncate">{s.address}</div>
      </div>
      <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${ok ? "bg-[#005F19]/10 text-[#005F19]" : "bg-[#0E395F]/10 text-[#0E395F]"}`}>
        {ok ? "Approved" : "Declined"}
      </span>
    </div>
  );
}

export default function AdminCourts() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const [events, setEvents] = useState(null);
  const [evBusy, setEvBusy] = useState(null);

  const load = async () => {
    try {
      const [courtsRes, eventsRes] = await Promise.all([
        http.get("/admin/court-submissions"),
        http.get("/admin/events"),
      ]);
      setItems(courtsRes.data);
      setEvents(eventsRes.data);
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  useEffect(() => { if (user) load(); }, [user]);

  if (user && user.role !== "admin") {
    return (
      <div className="max-w-xl mx-auto py-20 text-center" data-testid="admin-forbidden">
        <ShieldCheck className="mx-auto pv-text-dark mb-3" size={32} />
        <h1 className="text-xl font-black pv-text-dark">Admins only</h1>
        <p className="text-sm text-black/50 mt-1">This area is reserved for PaddleVerse admins.</p>
      </div>
    );
  }
  if (!items) return <div className="grid place-items-center py-24"><Loader2 className="animate-spin pv-text-dark" /></div>;

  const act = async (s, action) => {
    setBusy(s.submission_id + action);
    try {
      await http.post(`/admin/court-submissions/${s.submission_id}/${action}`);
      toast.success(action === "approve" ? `Approved — ${s.name} added to Court Radar` : `Declined ${s.name}`);
      load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(null);
    }
  };

  const actEvent = async (ev, action) => {
    setEvBusy(ev.event_id + action);
    try {
      await http.post(`/admin/events/${ev.event_id}/${action}`);
      toast.success(action === "approve" ? `Published — ${ev.title} is now live` : `Declined ${ev.title}`);
      load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setEvBusy(null);
    }
  };

  const pending = items.filter((i) => i.status === "pending");
  const reviewed = items.filter((i) => i.status !== "pending");
  const pendingEvents = (events || []).filter((e) => e.status === "pending");
  const reviewedEvents = (events || []).filter((e) => e.status !== "pending");

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="admin-courts-page">
      <div>
        <h1 className="text-3xl font-black font-display pv-text-dark flex items-center gap-2"><ShieldCheck size={26} className="pv-text-support" />Review Queue</h1>
        <p className="text-sm text-black/50">Approve community-submitted courts and events.</p>
      </div>

      <div data-testid="admin-events-section">
        <div className="text-xs font-bold uppercase tracking-wider text-black/40 mb-3 flex items-center gap-2"><CalendarDays size={13} /> Events pending ({pendingEvents.length})</div>
        {pendingEvents.length === 0 && <div className="rounded-2xl bg-white border border-black/5 p-6 text-sm text-black/50" data-testid="no-pending-events">No events waiting for review.</div>}
        <div className="space-y-4">
          {pendingEvents.map((ev) => <EventReviewCard key={ev.event_id} ev={ev} act={actEvent} busy={evBusy} />)}
        </div>
        {reviewedEvents.length > 0 && (
          <div className="mt-4">
            <div className="text-xs font-bold uppercase tracking-wider text-black/40 mb-3">Reviewed events</div>
            <div className="space-y-3">
              {reviewedEvents.map((ev) => (
                <div key={ev.event_id} data-testid="admin-reviewed-event" className="flex items-center justify-between gap-3 rounded-xl border border-black/5 bg-white px-4 py-3">
                  <div className="min-w-0"><div className="font-bold pv-text-dark text-sm truncate">{ev.title}</div><div className="text-xs text-black/50 truncate">{ev.type_label} · {ev.host_name}</div></div>
                  <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${ev.status === "published" ? "bg-[#005F19]/10 text-[#005F19]" : "bg-[#0E395F]/10 text-[#0E395F]"}`}>{ev.status === "published" ? "Published" : "Declined"}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-black/40 mb-3 flex items-center gap-2"><MapPin size={13} /> Courts pending ({pending.length})</div>
        {pending.length === 0 && <div className="rounded-2xl bg-white border border-black/5 p-6 text-sm text-black/50" data-testid="no-pending">No submissions waiting for review.</div>}
        <div className="space-y-4">
          {pending.map((s) => <Card key={s.submission_id} s={s} act={act} busy={busy} />)}
        </div>
      </div>

      {reviewed.length > 0 && (
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-black/40 mb-3">Reviewed courts</div>
          <div className="space-y-3">
            {reviewed.map((s) => <ReviewedRow key={s.submission_id} s={s} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function EventReviewCard({ ev, act, busy }) {
  return (
    <div data-testid="admin-event-card" className="rounded-2xl bg-white border border-black/5 shadow-sm overflow-hidden sm:flex">
      {ev.photo_url ? (
        <img src={ev.photo_url} alt={ev.title} className="sm:w-48 w-full h-40 object-cover" />
      ) : (
        <div className="sm:w-48 w-full h-40 bg-[#0E395F]/5 grid place-items-center text-black/25"><CalendarDays size={28} /></div>
      )}
      <div className="p-5 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="rounded-full bg-[#E4572E]/12 text-[#E4572E] font-black px-2.5 py-1 text-xs uppercase">{ev.type_label}</span>
          {ev.price ? <span className="rounded-full bg-black/5 text-black/70 px-2.5 py-1 text-xs font-bold inline-flex items-center gap-0.5"><DollarSign size={11} />{ev.price}</span> : null}
        </div>
        <div className="font-black pv-text-dark text-lg mt-2">{ev.title}</div>
        {ev.description && <p className="text-sm text-black/60 mt-1">{ev.description}</p>}
        <div className="flex flex-wrap gap-3 mt-2 text-xs text-black/55">
          {(ev.day || ev.time) && <span className="inline-flex items-center gap-1"><CalendarDays size={12} />{[ev.day, ev.time].filter(Boolean).join(" · ")}</span>}
          <span className="inline-flex items-center gap-1"><MapPin size={12} />{ev.court_name || ev.city || "TBD"}</span>
          <span className="inline-flex items-center gap-1"><Users size={12} />Cap {ev.capacity}</span>
          {(ev.skill_min || ev.skill_max) && <span>DUPR {ev.skill_min ?? "—"}–{ev.skill_max ?? "—"}</span>}
        </div>
        <div className="text-xs text-black/40 mt-2">Hosted by {ev.host_name}</div>
        <div className="flex gap-3 mt-4">
          <Button data-testid={`event-approve-${ev.event_id}`} onClick={() => act(ev, "approve")} disabled={!!busy} className="bg-[#005F19] text-white font-bold rounded-full px-5 hover:opacity-90">
            {busy === ev.event_id + "approve" ? <Loader2 size={15} className="animate-spin mr-2" /> : <Check size={15} className="mr-2" />}Publish
          </Button>
          <Button data-testid={`event-decline-${ev.event_id}`} onClick={() => act(ev, "decline")} disabled={!!busy} variant="outline" className="border-2 border-black/15 text-black/60 font-bold rounded-full px-5 hover:bg-black/5">
            {busy === ev.event_id + "decline" ? <Loader2 size={15} className="animate-spin mr-2" /> : <X size={15} className="mr-2" />}Decline
          </Button>
        </div>
      </div>
    </div>
  );
}
